import React, { useState } from "react"
import {
  Box, Collapse, Divider, Flex, Icon as ChakraIcon, IconProps, Skeleton, Stack, Text, chakra,
} from "@chakra-ui/react"
import { useHistory } from "react-router-dom"
import Icon from "components/elements/Icon"
import DashboardCard from "../components/DashboardCard"
import { Bar } from "../components/charts"
import { EventPhase } from "../phase"

export type ChecklistStep = {
  id: string
  label: string
  done: boolean
  hint?: string
  onClick?: () => void
  to?: string
}

export type ChecklistTicks = Record<string, boolean>
type OnTick = (id: string, done: boolean | null) => void

const LINE_ICON = {
  fill: "none", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round",
} as const

const Circle = ({ done }: { done: boolean }) => (done ? (
  <Flex boxSize={4} borderRadius="full" bg="green.500" color="white" align="center" justify="center">
    <ChakraIcon viewBox="0 0 24 24" boxSize="10px" strokeWidth={1.5} data-icon="check" aria-hidden {...LINE_ICON}>
      <path d="M20 6 9 17l-5-5" />
    </ChakraIcon>
  </Flex>
) : (
  <Box boxSize={4} borderRadius="full" borderWidth={2} borderColor="gray.300" _hover={{ borderColor: "gray.500" }} />
))

const Chevron = (props: IconProps) => (
  <ChakraIcon viewBox="0 0 24 24" boxSize="14px" strokeWidth={1.5} flexShrink={0} aria-hidden {...LINE_ICON} {...props}>
    <path d="m9 6 6 6-6 6" />
  </ChakraIcon>
)

const Step = ({ step, done, onTick }: { step: ChecklistStep, done: boolean, onTick?: OnTick }) => {
  const history = useHistory()
  const go = () => (step.to ? history.push(step.to) : step.onClick?.())
  // A tick that matches what the data says goes back to automatic.
  const toggle = () => onTick?.(step.id, !done === step.done ? null : !done)

  return (
    <Flex
      align="center"
      gap={2.5}
      w="calc(100% + 16px)"
      minH={9}
      mx={-2}
      px={2}
      borderRadius="lg"
      role="group"
      _hover={{ bg: "gray.50" }}
    >
      {onTick ? (
        <chakra.button
          type="button"
          onClick={toggle}
          aria-label={done ? "Mark incomplete" : "Mark complete"}
          m={-1.5}
          p={1.5}
          flexShrink={0}
        >
          <Circle done={done} />
        </chakra.button>
      ) : (
        <Box flexShrink={0}><Circle done={done} /></Box>
      )}
      <chakra.button
        type="button"
        onClick={go}
        display="flex"
        alignItems="center"
        gap={2.5}
        flex={1}
        minW={0}
        py={1}
        textAlign="left"
      >
        <Box flex={1} minW={0}>
          <Text color={done ? "gray.600" : "gray.900"} textDecoration={done ? "line-through" : "none"}>
            {step.label}
          </Text>
          {step.hint && <Text fontSize="xs" color="gray.600">{step.hint}</Text>}
        </Box>
        <Chevron color="gray.500" _groupHover={{ color: "gray.700" }} data-testid="step-chevron" />
      </chakra.button>
    </Flex>
  )
}

type Props = {
  phase: EventPhase
  steps: ChecklistStep[]
  optionalSteps: ChecklistStep[]
  loading?: boolean
  ticks?: ChecklistTicks
  onTick?: OnTick
}

const ChecklistCard = ({
  phase, steps, optionalSteps: allOptionalSteps, loading = false, ticks = {}, onTick,
}: Props) => {
  const [showOptional, setShowOptional] = useState(false)
  const [review, setReview] = useState(false)
  const optionalSteps = phase === "pre-event" ? allOptionalSteps : []
  const isDone = (step: ChecklistStep) => ticks[step.id] ?? step.done
  const done = steps.filter(isDone).length
  const complete = done === steps.length
  const optionalLeft = optionalSteps.filter((step) => !isDone(step)).length
  const row = (step: ChecklistStep) => (
    <Step key={step.id} step={step} done={isDone(step)} onTick={onTick} />
  )

  if (loading) {
    return (
      <DashboardCard title="Setup Checklist">
        <Skeleton h={24} borderRadius="lg" aria-label="Loading setup checklist" />
      </DashboardCard>
    )
  }

  if (complete && !review) {
    return (
      <DashboardCard
        title="Setup Checklist"
        action={<Text fontSize="xs" color="gray.600" pt={1}>Complete</Text>}
      >
        <Flex align="center" gap={3}>
          <Flex boxSize={8} borderRadius="full" bg="green.500" color="white" align="center" justify="center" flexShrink={0}>
            <Icon icon="checkmark" size={4} />
          </Flex>
          <Box flex={1}>
            <Text fontWeight="semibold">Setup complete</Text>
            <Text fontSize="xs" color="gray.600">All {steps.length} steps done</Text>
          </Box>
          <chakra.button type="button" onClick={() => setReview(true)} fontSize="xs" fontWeight="semibold" px={2.5} h={7} borderWidth={1} borderRadius="lg" _hover={{ bg: "gray.50" }}>
            Review
          </chakra.button>
        </Flex>
        {optionalLeft > 0 && (
          <>
            <Divider my={3} />
            <chakra.button type="button" onClick={() => { setReview(true); setShowOptional(true) }} fontSize="xs" fontWeight="semibold" color="gray.600" textAlign="left">
              {optionalLeft} optional {optionalLeft === 1 ? "step" : "steps"} left
            </chakra.button>
          </>
        )}
      </DashboardCard>
    )
  }

  return (
    <DashboardCard
      title="Setup Checklist"
      action={<Text fontSize="xs" color="gray.600" pt={1} whiteSpace="nowrap">{done} of {steps.length} complete</Text>}
    >
      <Box mb={3}>
        <Bar pct={(done / steps.length) * 100} color="gray.900" height={1.5} />
      </Box>
      {complete && (
        <Flex justify="flex-end" mt={-1} mb={1}>
          <chakra.button type="button" onClick={() => setReview(false)} fontSize="xs" fontWeight="semibold" color="gray.600" _hover={{ color: "gray.900" }}>
            Collapse
          </chakra.button>
        </Flex>
      )}
      <Stack spacing={0}>
        {steps.map(row)}
      </Stack>
      {optionalSteps.length > 0 && (
        <>
          <Divider my={2} />
          <chakra.button
            type="button"
            onClick={() => setShowOptional((open) => !open)}
            display="flex"
            alignItems="center"
            gap={2}
            h={9}
            fontSize="xs"
            fontWeight="semibold"
            color="gray.600"
            _hover={{ color: "gray.900" }}
          >
            <Chevron transform={showOptional ? "rotate(90deg)" : undefined} transition="transform .15s" />
            {showOptional ? "Hide optional steps" : `Show ${optionalLeft} optional ${optionalLeft === 1 ? "step" : "steps"}`}
          </chakra.button>
          <Collapse in={showOptional} animateOpacity>
            <Stack spacing={0}>
              {optionalSteps.map(row)}
            </Stack>
          </Collapse>
        </>
      )}
    </DashboardCard>
  )
}

export default ChecklistCard
